/**
 * WorkspaceTabs — route element wrappers for every ProjectWorkspace tab.
 *
 * FU-001 (2026-05-27): ProjectWorkspace migrated from a `?tab=` query
 * param + inline `renderTabContent()` switch to React-Router child
 * routes with `<Outlet />`. Each tab is now a route element; this file
 * is where those route elements live so ProjectWorkspace stays focused
 * on chrome + state, not tab dispatch.
 *
 * Every wrapper reads its workspace state via `useOutletContext()` and
 * passes it to the underlying tab component unchanged. The wrappers
 * preserve the previous lazy/static import split: tabs that were lazy
 * before stay lazy (Case 6 — tab/route splitting per
 * feedback_no_lazy_imports).
 */

import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { ConfigurationProvider } from '../../../contexts/ConfigurationContext';
import { LoadingState } from '../../../components/ui/LoadingState';
import { Skeleton } from '../../../components/ui/Skeleton';
import { loadingBarStart } from '../../../services/loadingBar';
import styles from './WorkspaceTabs.module.css';
import { WorkflowMachineProvider } from '../../athena/contexts/WorkflowMachineContext';
import { useWorkflowSession } from '../../athena/contexts/WorkflowSessionContext';

// Static — first-paint critical or small.
import OverviewV2 from '../../overview/OverviewV2';
import WorkflowV2 from '../../athena/WorkflowV2';
import UNR155ChecklistPage from '../../unr155-checklist/components/UNR155ChecklistPage';
import { WorkflowVersions } from '../../workflow-versions';
import { ResidualTARAV2 } from '../../residual-tara-v2';
import ThreatTraceabilityPage from '../../traceability/components/ThreatTraceabilityPage';

// Lazy — tab content only loaded when the tab is active.
const ArchitectureDiagramListPage = lazy(() => import('../../architecture-diagrams/ArchitectureDiagramListPage'));
const ProjectIntegrationsTab = lazy(() => import('../../integrations/ProjectIntegrationsTab'));
const AdminConfigurationPage = lazy(() => import('../../admin-configuration/AdminConfigurationPage'));
const AttackTreeList = lazy(() => import('../../attack-trees/AttackTreeList'));
const ReportsPage = lazy(() => import('../../reports/components/ReportsPage'));
const ThreatTraceabilityMap = lazy(() => import('../../reports/components/ThreatTraceabilityMap'));
const ComplianceMappingSection = lazy(() => import('../../reports/components/ComplianceMappingSection'));
const AttackTreeVisualization = lazy(() => import('../../attack-trees/AttackTreeVisualization'));
const ArchitectureDiagramPage = lazy(() => import('../../architecture-diagrams/ArchitectureDiagramPage'));

// Suspense fallback for lazy tabs — holds a loading-bar token while the
// chunk loads so the top progress bar runs during tab transitions.
const TabFallback = () => {
  useEffect(() => loadingBarStart(), []);
  return <LoadingState variant="page" />;
};

const TabLoadingFallback = <TabFallback />;

// Seeds the WorkflowSessionContext with the project's session/thread
// IDs so ResidualTARAV2 (and any future consumers) can find them via
// session.backendSession.sessionId. Story 4.2 migrated this from
// useWorkflowMachine → useWorkflowSession for the Session slice.
// The legacy Step10 fallback was removed when v1 was sunset.
const ResidualTARABridge = ({ project, children }) => {
  const session = useWorkflowSession();
  const [ready, setReady] = useState(false);
  const seeded = useRef(false);

  useEffect(() => {
    if (seeded.current) return;
    if (!project?.session_id) return;
    seeded.current = true;
    session.updateBackendSession({
      sessionId: project.session_id,
      threadId: project.thread_id,
      tenantId: project.tenant_id,
    });
    // Allow one render cycle for the context to propagate the session
    requestAnimationFrame(() => setReady(true));
  }, [project, session]);

  if (!ready) {
    // Rail + content skeleton — covers both the project-document fetch
    // (project=null) and the one-frame seeding window; no spinner.
    return (
      <div className={styles.bridgeSkeleton} aria-hidden="true">
        <Skeleton className={styles.bridgeRail} />
        <Skeleton className={styles.bridgeContent} />
      </div>
    );
  }

  return children;
};

// ─── Tab loading ownership contract ──────────────────────────────────
// Every tab mounts DURING the workspace's getSession fetch and owns its
// loading UI. The outlet context provides `projectLoading` (true while
// the project document is in flight); `project` is null in that window,
// so a missing `project.thread_id` is AMBIGUOUS — it can mean "still
// loading" or "no workflow exists yet". Tabs whose data keys on project
// fields must check projectLoading (skeleton) before any no-thread
// empty state, and must not fire requests with undefined ids. Tabs
// keyed only on the URL's projectId can fetch immediately — their
// request runs in parallel with getSession.

// ─── Tab wrappers ────────────────────────────────────────────────────

export const OverviewTab = () => {
  const { project, projectId, projectLoading } = useOutletContext();
  return (
    <OverviewV2
      project={project}
      threadId={project?.thread_id}
      sessionId={projectId}
      projectLoading={projectLoading}
    />
  );
};

export const TaraWorkflowTab = () => {
  const { project, projectId, projectLoading } = useOutletContext();
  // WorkflowV2 treats a missing threadId as "brand-new project" and
  // renders the Step 0 setup flow — gate at the wrapper while the
  // project document loads so an in-progress project can't flash the
  // start-analysis view (or expose its Start button) mid-fetch.
  if (projectLoading) {
    return (
      <div className={styles.workflowSkeleton} aria-hidden="true">
        <div className={styles.workflowRail}>
          {Array.from({ length: 9 }).map((_, i) => (
            <Skeleton key={i} className={styles.workflowStepPill} />
          ))}
        </div>
        <Skeleton className={styles.workflowContent} />
      </div>
    );
  }
  return (
    <WorkflowV2
      projectId={projectId}
      threadId={project?.thread_id}
      tenantId={project?.tenant_id}
      analysisMode={project?.analysis_mode}
      fileId={project?.file_id}
    />
  );
};

export const ArchitectureTab = () => {
  const { projectId } = useOutletContext();
  return (
    <Suspense fallback={TabLoadingFallback}>
      <ArchitectureDiagramListPage projectId={projectId} />
    </Suspense>
  );
};

export const UNR155ChecklistTab = () => {
  const { projectId } = useOutletContext();
  return <UNR155ChecklistPage embedded projectId={projectId} />;
};

export const IntegrationsTab = () => {
  const { projectId } = useOutletContext();
  return (
    <Suspense fallback={TabLoadingFallback}>
      <ProjectIntegrationsTab projectId={projectId} />
    </Suspense>
  );
};

export const ConfigurationTab = () => {
  const { projectId } = useOutletContext();
  return (
    <Suspense fallback={TabLoadingFallback}>
      <ConfigurationProvider sessionId={projectId}>
        <AdminConfigurationPage embedded />
      </ConfigurationProvider>
    </Suspense>
  );
};

export const AttackTreesTab = () => {
  const { project, projectLoading } = useOutletContext();
  return (
    <Suspense fallback={TabLoadingFallback}>
      <AttackTreeList
        embedded
        threadId={project?.thread_id}
        projectLoading={projectLoading}
      />
    </Suspense>
  );
};

export const ReportsTab = () => {
  const { project, projectLoading } = useOutletContext();
  return (
    <Suspense fallback={TabLoadingFallback}>
      <ReportsPage analysisId={project?.thread_id} projectLoading={projectLoading} />
    </Suspense>
  );
};

export const VersionsTab = () => {
  const { project, projectLoading, setAgentResults, fetchWorkflowData } = useOutletContext();
  return (
    <WorkflowVersions
      threadId={project?.thread_id}
      sessionId={project?.session_id}
      workflowStatus={project?.session_status}
      projectLoading={projectLoading}
      onAfterRestore={() => {
        // Drop the cached agent results so the next tab visit
        // re-fetches the freshly-restored state.
        setAgentResults({});
        if (project?.thread_id) {
          fetchWorkflowData(project.thread_id);
        }
      }}
    />
  );
};

export const TraceabilityTab = () => {
  const { project, projectLoading } = useOutletContext();
  return (
    <ThreatTraceabilityPage
      threadId={project?.thread_id}
      projectLoading={projectLoading}
    />
  );
};

export const TraceabilityMapTab = () => {
  const { project, projectLoading } = useOutletContext();
  return (
    <Suspense fallback={TabLoadingFallback}>
      <ThreatTraceabilityMap
        threadId={project?.thread_id}
        projectLoading={projectLoading}
      />
    </Suspense>
  );
};

export const ResidualTaraTab = () => {
  const { project, projectId } = useOutletContext();
  return (
    <WorkflowMachineProvider key={projectId}>
      <ResidualTARABridge project={project}>
        <ResidualTARAV2 />
      </ResidualTARABridge>
    </WorkflowMachineProvider>
  );
};

export const ComplianceMappingTab = () => {
  const { project, projectLoading } = useOutletContext();
  return (
    <Suspense fallback={TabLoadingFallback}>
      <ComplianceMappingSection
        threadId={project?.thread_id}
        projectLoading={projectLoading}
      />
    </Suspense>
  );
};

// ─── Editor routes (nested under their list-page sibling) ────────────

export const AttackTreeEditorTab = () => {
  const { project, projectId, projectLoading } = useOutletContext();
  const { threatId } = useParams();
  // Editor fetches by thread_id, which doesn't exist until getSession
  // resolves — gate at the wrapper with a canvas-sized placeholder
  // instead of mounting the visualization with an undefined id.
  if (projectLoading) {
    return <Skeleton className={styles.canvasSkeleton} aria-hidden="true" />;
  }
  return (
    <Suspense fallback={TabLoadingFallback}>
      <AttackTreeVisualization
        threadId={project?.thread_id}
        threatId={threatId}
        projectId={projectId}
      />
    </Suspense>
  );
};

export const ArchitectureDiagramEditorTab = () => {
  const { projectId } = useOutletContext();
  const { diagramId } = useParams();
  return (
    <Suspense fallback={TabLoadingFallback}>
      <ArchitectureDiagramPage diagramId={diagramId} projectId={projectId} />
    </Suspense>
  );
};
